import * as React from 'react';

import getenv from 'util/getenv';

export interface TurnstileProps {
  onSuccess?: (token: string) => void;
  onError?: (errorCode?: string) => void;
  onExpired?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
  className?: string;
}

const sitekey = getenv('REACT_APP_TURNSTILE_SITEKEY');

const Turnstile: React.FC<TurnstileProps> = ({
  onSuccess,
  onError,
  onExpired,
  theme = 'auto',
  size = 'normal',
  className,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const widgetIdRef = React.useRef<string | null>(null);

  // Use refs for callbacks to prevent re-rendering the widget
  const onSuccessRef = React.useRef(onSuccess);
  const onErrorRef = React.useRef(onError);
  const onExpiredRef = React.useRef(onExpired);

  // Update refs when callbacks change
  React.useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    onExpiredRef.current = onExpired;
  }, [onSuccess, onError, onExpired]);

  React.useEffect(() => {
    if (!sitekey || !containerRef.current) return;

    const container = containerRef.current;

    const renderWidget = () => {
      if (typeof window === 'undefined' || !window.turnstile || !container) {
        return;
      }

      try {
        const widgetId = window.turnstile.render(container, {
          sitekey,
          theme,
          size,
          callback: (token: string) => {
            if (onSuccessRef.current) {
              onSuccessRef.current(token);
            }
          },
          'error-callback': (errorCode?: string) => {
            if (onErrorRef.current) {
              onErrorRef.current(errorCode);
            }
          },
          'expired-callback': () => {
            // Reset the widget when it expires
            if (widgetIdRef.current && window.turnstile) {
              try {
                window.turnstile.reset(widgetIdRef.current);
              } catch (err) {
                // Silently handle reset errors - widget will be re-rendered if needed
              }
            }
            if (onExpiredRef.current) {
              onExpiredRef.current();
            }
          },
        });

        widgetIdRef.current = widgetId;
      } catch (err) {
        // Widget rendering failed - component will handle gracefully by not rendering
      }
    };

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    if (typeof window !== 'undefined') {
      if (window.turnstile) {
        const turnstile = window.turnstile;
        if (turnstile.ready) {
          turnstile.ready(renderWidget);
        } else {
          renderWidget();
        }
      } else {
        const checkTurnstile = () => {
          if (window.turnstile) {
            const turnstile = window.turnstile;
            if (turnstile.ready) {
              turnstile.ready(renderWidget);
            } else {
              renderWidget();
            }
          } else {
            timeoutId = setTimeout(checkTurnstile, 50);
          }
        };
        checkTurnstile();
      }
    }

    // Cleanup: remove widget and clear timeout when component unmounts or dependencies change
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (err) {
          // Silently handle removal errors during cleanup
        }
        widgetIdRef.current = null;
      }
    };
  }, [theme, size]);

  if (!sitekey) {
    return null;
  }

  return <div ref={containerRef} className={className} />;
};

export default Turnstile;
